import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TablaVariacionMensualVista } from '../../vista/tablaVariacionMensualVista';
import { useCargaMasivaMatriz } from '../useCargaMasivaMatriz';
import type { IConfiguracionVariacionMensual } from '../../interfaces';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';

interface VariacionMensualCVProps {
  datosIniciales?: IConfiguracionVariacionMensual[];
  onRegistrarCancelacion?: (key: string, canceler: () => void) => void;
  onIniciarEdicionGeneral?: () => void;
}
import { usePaisConfiguracion } from '../paisConfiguracion';
import { esFilaNueva, idFilaNueva } from '../filaNuevaMatriz';

export const VariacionMensualCV: React.FC<VariacionMensualCVProps> = ({
  datosIniciales = [],
  onRegistrarCancelacion,
  onIniciarEdicionGeneral
}) => {
  const [variacionMensual, setVariacionMensual] = useState<IConfiguracionVariacionMensual[]>(datosIniciales);
  const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
  const [respaldoFila, setRespaldoFila] = useState<any>(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  // Se incrementa tras una carga masiva para volver a consultar la matriz.
  const [versionDatos, setVersionDatos] = useState(0);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const modelo = new CotizadorModelo();
        const res = await modelo.obtenerMatrices<any>('variacion-mensual', paisId as number);
        if (res?.Data) {
          const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
          setVariacionMensual(res.Data.map((item: any) => ({
             Id: String(item.id || item.Id), 
             Mes: MESES[item.mesNumero || item.MesNumero - 1] || String(item.mesNumero || item.MesNumero), 
             Porcentaje: item.porcentaje || item.Porcentaje 
          })));
        }
      } catch (error) {
        avisarErrorConsulta(error, 'la variación mensual del flete');
      }
    };
    cargarDatos();
  }, [paisId, versionDatos]);

  const mostrarNotificacionObligatorio = useCallback(() => {
    setIntentoGuardar(true);
    mostrarNotificacion(
      EnumTipoNotificacion.Warning,
      'Campos Obligatorios',
      'Debes diligenciar todos los campos obligatorios antes de guardar la fila.'
    );
  }, [mostrarNotificacion]);

  const respaldoFilaRef = useRef(respaldoFila);
  useEffect(() => {
    respaldoFilaRef.current = respaldoFila;
  }, [respaldoFila]);

  const cancelarEdicion = useCallback(() => {
    setIntentoGuardar(false);
    setFilaEnEdicion(null);
    const respaldo = respaldoFilaRef.current;
    if (respaldo) {
      if (respaldo.__esNueva || esFilaNueva(respaldo.Id)) {
        setVariacionMensual(prev => prev.filter(f => f.Id !== respaldo.Id));
      } else {
        const { __esNueva, ...limpio } = respaldo;
        setVariacionMensual(prev => prev.map(f => f.Id === limpio.Id ? limpio : f));
      }
      setRespaldoFila(null);
    }
  }, []);

  useEffect(() => {
    onRegistrarCancelacion?.('variacionMensual', cancelarEdicion);
  }, [onRegistrarCancelacion, cancelarEdicion]);

  const actualizarFila = useCallback((id: string, campo: string, valor: string | number) => {
    setVariacionMensual(prev => {
      const existe = prev.some(fila => fila.Id === id);
      if (existe) {
        return prev.map(fila => fila.Id === id ? { ...fila, [campo]: valor } : fila);
      } else {
        const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        const mesIndex = Number(id) - 1;
        const nombreMes = MESES[mesIndex] || id;
        return [...prev, { Id: id, Mes: nombreMes, Porcentaje: Number(valor) }];
      }
    });
  }, []);

  const eliminarFila = useCallback(async (id: string) => {
    const confirmado = await confirmar({
      titulo: 'Eliminar fila',
      mensaje: '¿Está seguro de que desea eliminar esta variación? Esta acción no se puede deshacer.',
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        const isNew = esFilaNueva(id);
        if (!isNew) {
          const modelo = new CotizadorModelo();
          await modelo.eliminarMatrizFila('variacion-mensual', Number(id));
        }
        setVariacionMensual(prev => prev.filter(fila => fila.Id !== id));
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Eliminada', 'La variación se eliminó correctamente.');
      } catch (error) {
        console.error(error);
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'Ocurrió un error al eliminar la variación.');
      }
    }
  }, [mostrarNotificacion, confirmar]);

  const agregarFila = useCallback(() => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    const nuevaFila: IConfiguracionVariacionMensual = {
      Id: idFilaNueva(),
      Mes: "", Porcentaje: "" as any
    };
    setVariacionMensual(prev => [nuevaFila, ...prev]);
    setRespaldoFila({ ...nuevaFila, __esNueva: true });
    setFilaEnEdicion(nuevaFila.Id);
  }, [onIniciarEdicionGeneral]);

  const guardarFila = useCallback(async (id: string) => {
    const filaToSave = variacionMensual.find(f => f.Id === id);
    if (!filaToSave) return;
    
    try {
      const modelo = new CotizadorModelo();
      const isNew = esFilaNueva(id);
      const dbId = isNew ? null : Number(id);

      const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
      let mesNumero = MESES.indexOf(String(filaToSave.Mes)) + 1;
      if (mesNumero === 0) mesNumero = Number(filaToSave.Mes);

      const payload = {
        PaisId: paisId,
        MesNumero: mesNumero,
        Porcentaje: Number(filaToSave.Porcentaje)
      };

      const res = await modelo.guardarMatrizFila('variacion-mensual', dbId, payload);

      if (res?.NotificationType === 1) { // Success
        if (isNew && res.Data) {
          setVariacionMensual(prev => prev.map(f => f.Id === id ? { ...f, Id: String(res.Data) } : f));
        }
        setIntentoGuardar(false);
        setFilaEnEdicion(null);
        setRespaldoFila(null);
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Guardada', 'La variación se guardó correctamente.');
      } else {
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', res?.Messages?.[0] || 'No se pudo guardar la fila.');
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la fila.');
    }
  }, [variacionMensual, mostrarNotificacion]);

  const iniciarEdicion = useCallback((fila: any) => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    setRespaldoFila({ ...fila });
    setFilaEnEdicion(fila.Id);
  }, [onIniciarEdicionGeneral]);

  const { descargarPlantilla, cargaMasiva, modalResultado } = useCargaMasivaMatriz({
    matriz: 'variacion-mensual',
    etiquetaReferencia: 'Mes',
    antesDeCargar: () => { if (filaEnEdicion) cancelarEdicion(); },
    alTerminar: () => setVersionDatos(v => v + 1)
  });

  return (
    <>
    {modalResultado}
    <TablaVariacionMensualVista
      alRecargar={() => { cancelarEdicion(); setVersionDatos(v => v + 1); }}
      alDescargarPlantilla={descargarPlantilla}
      alCargaMasiva={cargaMasiva}
      datos={variacionMensual}
      actualizarFila={actualizarFila}
      eliminarFila={eliminarFila}
      guardarFila={guardarFila}
      filaEnEdicion={filaEnEdicion}
      iniciarEdicion={iniciarEdicion}
      cancelarEdicion={cancelarEdicion}
      agregarFila={agregarFila}
      mostrarNotificacionObligatorio={mostrarNotificacionObligatorio}
      intentoGuardar={intentoGuardar}
    />
    </>
  );
};
